import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import {
  doc,
  onSnapshot,
  DocumentData,
  DocumentSnapshot,
} from "firebase/firestore";
import { datadogRum } from "@datadog/browser-rum";
import { db } from "../../conf/firebaseConfig";
import { useQueryClient } from "@tanstack/react-query";
import { useLatestPrescriptionQuery } from "./usePrescriptionQuery";
import { Prescription } from "../../typings";
import { isEqual } from "../../utils/common";

export const useLatestPrescriptionRealtime = (
  overridePrescriptionId?: string
) => {
  const { data } = useLatestPrescriptionQuery();
  const prescriptionId = overridePrescriptionId || data?.prescriptionId;

  const [latestPrescription, setLatestPrescription] = useState<
    Prescription | undefined
  >();
  const [latestPrescriptionId, setLatestPrescriptionId] = useState<
    string | undefined
  >(undefined);
  const [error, setError] = useState<Error | null>(null);

  const queryClient = useQueryClient();
  const unsubscribeRef = useRef<(() => void) | undefined>();
  const prevPrescriptionIdRef = useRef<string | undefined>();

  const queryKey = useMemo(
    () => ["latest prescription realtime", { prescriptionId }],
    [prescriptionId]
  );

  const updatePrescriptionData = useCallback(
    (newData: Prescription | undefined, newId?: string) => {
      // keep react-query in sync (still just storing the doc data here)
      queryClient.setQueryData(queryKey, newData);

      // only set if changed
      setLatestPrescription((prev) =>
        isEqual(prev, newData) ? prev : newData
      );

      // track the current doc id separately so callers can use it
      setLatestPrescriptionId((prevId) => (prevId === newId ? prevId : newId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, queryKey, latestPrescription] // latestPrescription intentionally included
  );

  const handleSnapshot = useCallback(
    (snap: DocumentSnapshot<DocumentData>) => {
      const newData = snap.data() as Prescription | undefined;
      const newId = snap.id; // <-- capture the firestore document id
      updatePrescriptionData(newData, newId);
    },
    [updatePrescriptionData]
  );

  const manageListener = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = undefined;
    }

    if (prescriptionId) {
      unsubscribeRef.current = onSnapshot(
        doc(db, "steadymd.prescriptions", prescriptionId),
        handleSnapshot,
        (err) => {
          console.error("Error in Firestore listener:", err);

          // Track error in Datadog RUM for monitoring
          datadogRum.addError(err, {
            hook: "useLatestPrescriptionRealtime",
            collection: "steadymd.prescriptions",
            prescriptionId,
            errorCode: err.code,
            errorType: "firestore_listener_error",
          });

          setError(err);
        }
      );
    } else {
      // if we no longer have a prescription id, clear local state
      updatePrescriptionData(undefined, undefined);
    }
  }, [prescriptionId, handleSnapshot, updatePrescriptionData]);

  useEffect(() => {
    if (prescriptionId !== prevPrescriptionIdRef.current) {
      manageListener();
      prevPrescriptionIdRef.current = prescriptionId;
    }
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = undefined;
      }
    };
  }, [prescriptionId, manageListener]);

  if (!latestPrescription) return null;

  // Expose both the document data and the Firestore document id.
  return { ...latestPrescription, prescriptionId: latestPrescriptionId, error };
};
